import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Coins, 
  Sparkles, 
  Image, 
  Wand2, 
  Palette,
  ArrowLeft,
  Check,
  Eye,
  Zap,
  Gift
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const HowCreditsWorkPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const creditCosts = [
    { action: 'Prompt generation', cost: 1, icon: <Wand2 className="w-4 h-4" /> },
    { action: 'Regenerate prompt', cost: 1, icon: <Sparkles className="w-4 h-4" /> },
    { action: 'Mockup generation', cost: 2, icon: <Image className="w-4 h-4" /> },
    { action: 'Background generation', cost: 2, icon: <Palette className="w-4 h-4" /> },
  ];

  const usageExamples = [
    { text: 'Generate 50+ prompts', icon: <Wand2 className="w-4 h-4" /> },
    { text: 'Create 20+ mockups', icon: <Image className="w-4 h-4" /> },
    { text: 'Test multiple variations', icon: <Sparkles className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <span 
            className="font-semibold text-lg cursor-pointer" 
            onClick={() => navigate('/')}
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            MockupPro
          </span>
          <Button 
            size="sm"
            onClick={() => navigate('/auth')}
            style={{ backgroundColor: '#4F7CFF' }}
            data-testid="get-started-btn"
          >
            Get Started
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Coins className="w-8 h-8 text-white" />
          </div>
          <h1 
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="page-title"
          >
            How Credits Work
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
            Use credits to generate prompts, mockups, and designs — only pay for what you use.
          </p>
        </motion.div>

        {/* What is a Credit */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <Card>
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 
                    className="text-xl font-semibold mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    What is a Credit?
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    A credit is used each time you generate something on the platform, such as prompts, mockups, or backgrounds. Different actions use different amounts of credits based on complexity.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Credit Cost Table */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Credit Costs
          </h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {creditCosts.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 sm:p-5"
                    data-testid={`credit-cost-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                        {item.icon}
                      </div>
                      <span className="font-medium">{item.action}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-sm font-medium">
                      <Coins className="w-3.5 h-3.5" />
                      {item.cost} credit{item.cost > 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Usage Example */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            What Can You Do?
          </h2>
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                  <Coins className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">With just</p>
                  <p className="text-2xl font-bold" style={{ color: '#4F7CFF' }}>100 credits</p>
                </div>
              </div>
              <div className="space-y-3">
                {usageExamples.map((example, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <span className="text-foreground">{example.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Free Trial */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Free Trial Benefits
          </h2>
          <Card>
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium mb-1">Start with 120 free credits</p>
                  <p className="text-sm text-muted-foreground">
                    Explore all features without paying anything upfront.
                  </p>
                </div>
              </div>
              
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center mb-3">
                    <Coins className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="font-medium text-sm mb-1">Reduced Costs</p>
                  <p className="text-xs text-muted-foreground">Lower credit usage during trial period</p>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="font-medium text-sm mb-1">All Models</p>
                  <p className="text-xs text-muted-foreground">Try every AI model with limited uses</p>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
                    <Zap className="w-4 h-4 text-purple-600" />
                  </div>
                  <p className="font-medium text-sm mb-1">Full Features</p>
                  <p className="text-xs text-muted-foreground">Access to all platform tools</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Transparency Note */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-12"
        >
          <Card className="border-dashed">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium mb-1">Full Transparency</p>
                  <p className="text-sm text-muted-foreground">
                    You will always see how many credits each action uses before generating. No hidden costs, no surprises.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <Button 
            size="lg"
            onClick={() => navigate('/auth')}
            className="gap-2"
            style={{ backgroundColor: '#4F7CFF' }}
            data-testid="start-trial-btn"
          >
            <Sparkles className="w-4 h-4" />
            Start Free Trial
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            120 credits included • No credit card required
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-foreground/60 font-medium">
            © 2026 MockupPro. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm font-medium">
            <a href="/terms" className="text-foreground/70 hover:text-foreground transition-colors">Terms</a>
            <a href="/privacy" className="text-foreground/70 hover:text-foreground transition-colors">Privacy</a>
            <a href="/contact" className="text-foreground/70 hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowCreditsWorkPage;
